import PropTypes from "prop-types";
import { IconContainer, IconWrapper } from "./styles";

import { colors } from "../../../utils";
import { ViewOne } from "./styles";
import {
  Box,
  Button,
  Fade,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { transformedText } from "../../../utils/helpers";
import { FaPencil } from "react-icons/fa6";
import useScreenSize from "../../../hooks/useScreenSize";
import { transformEditorText } from "../../../utils/helpers";
import { isVideoUrl } from "../../../utils/validations";
import React, { useState } from "react";
import { FaEllipsisV } from "react-icons/fa";
import Edit from "@assets/images/editIcon.svg";
import { selectIsUserProfile } from "../../../redux/reducers/gallery";
import { useDispatch, useSelector } from "react-redux";
import FlagIcon from "@mui/icons-material/Flag";
import {
  setReportInstance,
  setReportLocation,
  setReportModalOpen,
} from "../../../redux/actions";
import {
  closeDialogPopup,
  openDialogPopup,
} from "../../../redux/reducers/dialogPopup";
import styled from "styled-components";
import { ReactComponent as Premium } from "@assets/images/PremiumSubscriptionIcon.svg";
import { useIfUserEligible } from "../../../hooks/useIfUserEligible";
import { FEATURES } from "../../../constants/features";
import { is } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { StyledTooltip } from "../../IyugaTooltip";
import LoopIcon from "@mui/icons-material/Loop";
import { ASSETS } from "../../../utils/config";
import { isUserLoggedIn } from "../../../utils/helpers";
import { openNonAuthenticatedModal } from "../../../redux/reducers/nonAuthenticatedModal";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { openUpgradePopup } from "../../../redux/reducers/upgradePopup";
import { getActiveSubscriptionData } from "../../../redux/selectors/subscriptionSelectors";

// Utility function to determine business title text based on user conditions
const getBusinessTitleText = (data, isDefault, userViewingType) => {
  // If not default, return the original business title
  if (!isDefault) {
    return data?.business_title;
  }

  // For default case, determine text based on user type
  switch (userViewingType) {
    case "premium_user_no_box":
      return `<p style="font-family: 'DM Sans'; font-weight: 700; font-size: 26px; line-height: 30px; letter-spacing: -1%; text-align: center; color: #123051;">AYMO!<br>(Are You Missing Out?)</p>`;
    case "free_user_no_box":
      return `<p style="font-family: 'DM Sans'; font-weight: 700; font-size: 28px; line-height: 30px; letter-spacing: -2%; text-align: center; color: #123051;">YAMO!<br>You Are Missing Out!</p>`;
    case "guest_user_no_box":
      return `<p style="font-family: 'DM Sans'; font-weight: 700; font-size: 30px; line-height: 30px; letter-spacing: -2%; text-align: center; color: #123051;">Welcome to iYuga!</p>`;
    default:
      // Fallback to original business title
      return data?.business_title;
  }
};

// Utility function to determine business description text based on user conditions
const getBusinessDescriptionText = (data, isDefault, userViewingType) => {
  // If not default, return the original description
  if (!isDefault) {
    return data?.description;
  }

  // For default case, determine text based on user type
  switch (userViewingType) {
    case "premium_user_no_box":
      return `<p style="font-family: 'DM Sans'; font-weight: 500; font-size: 16px; line-height: 24px; letter-spacing: -1%; text-align: center; color: #666E65; margin: 0;">Make the most out of your subscription! Join the Earnings Program, set up your Business Box, and get sharing, baby!</p>`;
    case "free_user_no_box":
      return `<p style="font-family: 'DM Sans'; font-weight: 500; font-size: 16px; line-height: 24px; letter-spacing: -1%; text-align: center; color: #666E65; margin: 0;">iYuga offers an Earnings Program for Premium and ProSuite subscribers!<br><br>Build, share, and earn – just a few paying referrals could cover your subscription cost!</p>`;
    case "guest_user_no_box":
      return `<p style="font-family: 'DM Sans'; font-weight: 500; font-size: 16px; line-height: 24px; letter-spacing: -1%; text-align: center; color: #666E65; margin: 0;">The new way to Bundle, Share, Engage.<br><br>Turn overwhelming and scattered content into simple, shareable Wheels that engage clients, build trust, and grow your business—without needing advanced tech skills or big budgets.<br><br>Login or Sign Up today!</p>`;
    default:
      // Fallback to original description
      return data?.description;
  }
};

// Create a styled span component
const ButtonContent = styled.span`
  p {
    margin: 0 !important;
    padding: 0 !important;
  }
`;

const DataBox = ({
  data,
  isGalleryBbox = false,
  onBboxEdit,
  isDefault = false,
  isEligible,
  onReplaceClick,
  isFormPreview,
  profileData,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isMobilePortrait } = useScreenSize();
  const isUserProfile = useSelector(selectIsUserProfile);
  const { data: currentPlan, loading: currentPlanLoading } = useSelector(
    getActiveSubscriptionData
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Determine user viewing type for consistent behavior across component
  const getUserViewingType = React.useCallback(() => {
    if (!isUserProfile && isUserLoggedIn() && currentPlan?.sub_tier > 1) {
      return "premium_user_no_box";
    }
    if (!isUserProfile && isUserLoggedIn() && currentPlan?.sub_tier < 2) {
      return "free_user_no_box";
    }
    if (!isUserLoggedIn() && !isDefault) {
      return "guest_user_with_box";
    }
    if (!isUserLoggedIn() && isDefault) {
      return "guest_user_no_box";
    }
    return "logged_in_user";
  }, [isUserProfile, currentPlan?.sub_tier, isDefault]);

  const [userViewingType, setUserViewingType] = useState(() =>
    getUserViewingType()
  );

  // Update user viewing type when dependencies change
  React.useEffect(() => {
    setUserViewingType(getUserViewingType());
  }, [getUserViewingType]);

  const iconsToShow = data?.icons.filter((icon) => icon.is_cta_enabled) || [];

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleReport = () => {
    const isLoggedIn = isUserLoggedIn();

    if (!isLoggedIn) {
      dispatch(
        openNonAuthenticatedModal({
          title: "Report Business Box?",
          action: "report",
          type: "business box",
          headerIcon: InfoOutlinedIcon, // Pass the component directly
          headerIconProps: {
            sx: { color: "#E54D2E", width: "24px", height: "24px" },
          }, // Pass styling as props
        })
      );
    } else {
      dispatch(setReportModalOpen(true));
      dispatch(setReportInstance(data?.id));
      dispatch(setReportLocation("BUSINESS_BOX"));
    }
    handleClose();
  };

  const handlePremiumDialog = () => {
    dispatch(
      openDialogPopup({
        isOpen: true,
        title: "Upgrade Personal Ad Space",
        text: "Want your own personal ad space? Upgrade to a Premium+ subscription to customize and showcase your own Business Box.",
        primaryButtonTitle: "Upgrade today!",
        secondaryButtonTitle: "Cancel",
        secondarButtonOnClick: () => dispatch(closeDialogPopup()),
        primaryButtonOnClick: () => {
          dispatch(closeDialogPopup());
          navigate("/settings?option=subscriptions");
        },
      })
    );
  };

  // Guest user with business box promotional box
  const GuestWithBoxPromo = ({ isFormPreview, isGalleryBbox }) => (
    <Box
      sx={{
        width: "350px",
        marginRight: "auto",
        marginLeft: "auto",
        minWidth: "100px",
        background: `linear-gradient(
          144.96deg,
          #ffffff 0%,
          #ffffff 35%,
          rgba(255, 255, 255, 0.9) 45%,
          rgba(204, 254, 255, 0.7) 55%,
          rgba(247, 254, 213, 0.7) 70%,
          rgba(253, 207, 254, 0.7) 85%,
          rgba(245, 238, 222, 0.8) 100%
        ),
        linear-gradient(0deg, rgba(255, 255, 255, 0.4) 0%, #ffffff 100%)`,
        padding: "24px",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        gap: "10px",
        border: "1px solid #BBBBBB",
        position: "relative",
        wordWrap: "break-word",
        overflowWrap: "break-word",
        whiteSpace: "normal",
        marginBottom: "16px",
      }}
    >
      {/* Title */}
      <Typography
        sx={{
          color: "#123051",
          fontSize: "22px",
          fontWeight: "700",
          lineHeight: "25px",
          textAlign: "center",
          width: "100%",
          wordWrap: "break-word",
          overflowWrap: "break-word",
          whiteSpace: "normal",
        }}
        dangerouslySetInnerHTML={{
          __html: `<p style="font-family: 'DM Sans'; font-weight: 700; font-size: 26px; line-height: 30px; letter-spacing: -2%; text-align: center; color: #123051;">Welcome to iYuga!</p>`,
        }}
      />

      {/* Description */}
      <Typography
        sx={{
          color: "#666E65",
          fontSize: "16px",
          lineHeight: "22px",
          letterSpacing: "-1%",
          width: "100%",
          textAlign: "center",
          "& p": {
            marginBottom: 0,
            marginTop: 0,
          },
        }}
        dangerouslySetInnerHTML={{
          __html: `<p style="font-family: 'DM Sans'; font-weight: 500; font-size: 16px; line-height: 24px; letter-spacing: -1%; text-align: center; color: #666E65; margin: 0;">The new way to Bundle, Share, Engage.<br><br>Turn overwhelming and scattered content into simple, shareable Wheels that engage clients, build trust, and grow your business—without needing advanced tech skills or big budgets.<br><br>Login or Sign Up today!</p>`,
        }}
      />

      {/* Buttons */}
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          width: "100%",
          marginTop: "10px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Button
          onClick={() => {
            window.location.href = "/login";
          }}
          sx={{
            width: "50%",
            height: "36px",
            border: "1px solid #9B5AF8",
            borderRadius: "8px",
            padding: "6px 12px",
            background: "white",
            color: "#9B5AF8",
            fontSize: "16px",
            fontWeight: "500",
            lineHeight: "20px",
            fontFamily: "DM Sans",
            textTransform: "none",
            "&:hover": {
              background: "#F8F4FF",
            },
          }}
        >
          Login
        </Button>

        <Button
          onClick={() => {
            const referralUrl = profileData?.data?.is_in_referral_program
              ? `/signup/${profileData?.data?.code}`
              : `/signup`;

            window.location.href = referralUrl;
          }}
          sx={{
            width: "50%",
            height: "36px",
            borderRadius: "8px",
            opacity: 1,
            padding: "8px 19px",
            gap: "10px",
            background: "#9B5AF8",
            color: "#FFFFFF",
            fontSize: "16px",
            fontWeight: "500",
            lineHeight: "20px",
            fontFamily: "DM Sans",
            textTransform: "none",
            "&:hover": {
              background: "#7A18E6",
            },
          }}
        >
          Sign Up
        </Button>
      </Box>
    </Box>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        // maxHeight: isFormPreview ? "none" : "100vh",
      }}
    >
      {/* Show promotional box for guest users with business box */}
      {userViewingType === "guest_user_with_box" && (
        <GuestWithBoxPromo
          isFormPreview={isFormPreview}
          isGalleryBbox={isGalleryBbox}
        />
      )}

      <ViewOne
        className="business-box"
        isMobilePortrait={isMobilePortrait}
        isGalleryBbox={isGalleryBbox}
        isDefault={isDefault}
        isFormPreview={isFormPreview}
      >
        {/* {isGalleryBbox && (
        <Button
          sx={{
            border: "1px solid",
            borderRadius: "2px",
            width: "100%",
            color: colors.PrimaryPurple,
            //backgroundColor: colors.secondary,
          }}
          onClick={onBboxEdit}
        >
          <span style={{ marginRight: "10px", lineHeight: "1px" }}>
            <FaPencil />
          </span>
          Edit
        </Button>
      )} */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {isGalleryBbox && isEligible && (isUserProfile || !isDefault) && (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "8px",
              }}
            >
              <IconButton
                style={{ color: "#123051", width: "24px", height: "24px" }}
                onClick={(event) => {
                  setAnchorEl(event.currentTarget);
                }}
              >
                <FaEllipsisV
                  color="#565556"
                  style={{ cursor: "pointer", width: "8px", height: "16px" }}
                />
              </IconButton>

              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                TransitionComponent={Fade}
                timeout={{ enter: 100, exit: 300 }}
                PaperProps={{
                  style: {
                    borderRadius: "8px",
                    width: "100px",
                    transition: "box-shadow 0.3s ease-in-out",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                {isUserProfile && (
                  <MenuItem
                    onClick={() => {
                      onBboxEdit();
                      handleClose();
                    }}
                    sx={{
                      fontSize: "14px",
                      color: "#333",
                      "&:hover": {
                        backgroundColor: "#f0f0f0",
                      },
                    }}
                  >
                    <img
                      style={{ marginRight: "0.2rem" }}
                      src={Edit}
                      className="img-fluid"
                      alt="Edit"
                      width="15"
                      height="15"
                    />
                    Edit
                  </MenuItem>
                )}
                {isUserProfile && (
                  <MenuItem
                    onClick={() => {
                      onReplaceClick();
                      handleClose();
                    }}
                    sx={{
                      fontSize: "14px",
                      color: "#333",
                      "&:hover": {
                        backgroundColor: "#f0f0f0",
                      },
                    }}
                  >
                    <LoopIcon
                      sx={{
                        marginRight: "0.2rem",
                        width: "18px",
                        height: "18px",
                        color: "#BBBBBB",
                      }}
                    />
                    Replace
                  </MenuItem>
                )}
                {!isUserProfile && (
                  <MenuItem
                    onClick={handleReport}
                    sx={{
                      fontSize: "14px",
                      color: "#333",
                      "&:hover": {
                        backgroundColor: "#f0f0f0",
                      },
                    }}
                  >
                    <FlagIcon
                      sx={{
                        marginRight: "0.2rem",
                        width: "18px",
                        height: "18px",
                        color: "#BBBBBB",
                      }}
                    />
                    Report
                  </MenuItem>
                )}
              </Menu>
            </div>
          )}
          {((isGalleryBbox && !isEligible) ||
            userViewingType === "free_user_no_box") && (
            <div
              style={{
                // position: "absolute",
                // top: "16px",
                // right: "16px",
                marginTop: "-10px",
                zIndex: 10,
              }}
            >
              <StyledTooltip
                title={"Premium Tier feature"}
                arrow
                placement="top"
                color={"#9256E7"}
                textColor={"#fff"}
              >
                <Premium
                  style={{ width: "20px", height: "20px", cursor: "pointer" }}
                  onClick={() => {
                    dispatch(
                      openUpgradePopup({
                        requiredSubscription: 2,
                      })
                    );
                  }}
                />
              </StyledTooltip>
            </div>
          )}
        </Box>

        {data?.imageSrc ? (
          isVideoUrl(data?.imageSrc) ? (
            <iframe
              src={data?.imageSrc}
              title={"preview video"}
              frameBorder="0"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                width: "100%",
                height: 190,
                minHeight: "9vw",
                display: "block",
                borderTopLeftRadius: "2px",
                borderTopRightRadius: "2px",
                borderRadius: "8px",
                // borderBottomLeftRadius: showVideoTitle && title ? 0 : borderRadius,
                // borderBottomRightRadius: showVideoTitle && title ? 0 : borderRadius,
              }}
            />
          ) : (
            <img
              src={data?.imageSrc}
              style={{
                width: "100%",
                objectFit: "cover",
                ...(!isDefault && { borderRadius: "8px", height: "200px" }),
              }}
              alt="image loading"
            />
          )
        ) : null}
        <Box sx={{ width: "100%" }}>
          <Typography
            sx={{
              // color: "#000000DE",
              color: colors.black,
              fontSize: "22px",
              //fontWeight: "bolder",
              lineHeight: "25px",
              //fontFamily: "DM Sans",
              width: "100%",
              wordWrap: "break-word",
              overflowWrap: "break-word",
              whiteSpace: "normal",
              letterSpacing: "-1%",
              mt: 1,
              textAlign: "center",

              ...(isDefault && {
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }),
            }}
            dangerouslySetInnerHTML={{
              __html: getBusinessTitleText(data, isDefault, userViewingType),
            }}
          />

          <Typography
            sx={{
              // color: "#000000DE",
              color: "#666E65",
              fontSize: "16px",
              //fontWeight: "500",
              lineHeight: "22px",
              // fontFamily: "DM Sans",
              letterSpacing: "-1%",
              width: "100%",
              mt: 1,
              textAlign: "center",
              "& p": {
                // Target all p tags within the Typography component
                marginBottom: 0,
                marginTop: 0, // Also reset top margin for consistency
              },
            }}
            dangerouslySetInnerHTML={{
              __html: transformEditorText(
                getBusinessDescriptionText(data, isDefault, userViewingType)
              ),
            }}
          />
          {/* {data?.description}
        </Typography> */}
        </Box>
        {isDefault && (
          <>
            {userViewingType === "premium_user_no_box" ? null : (
              <Box
                sx={{
                  display: "flex",
                  gap: "8px",
                  width: "100%",
                  marginTop: "10px",
                  ...(userViewingType === "guest_user_no_box"
                    ? {
                        flexDirection: "row",
                        justifyContent: "center",
                      }
                    : {
                        justifyContent: "center",
                      }),
                }}
              >
                {userViewingType === "guest_user_no_box" && (
                  <Button
                    onClick={() => {
                      // Navigate to login
                      window.location.href = "/login";
                    }}
                    sx={{
                      width: "50%",
                      height: "36px",
                      border: "1px solid #9B5AF8",
                      borderRadius: "8px",
                      padding: "6px 12px",
                      background: "white",
                      color: "#9B5AF8",
                      fontSize: "16px",
                      fontWeight: "500",
                      lineHeight: "20px",
                      fontFamily: "DM Sans",
                      textTransform: "none",
                      "&:hover": {
                        background: "#F8F4FF",
                      },
                    }}
                  >
                    Login
                  </Button>
                )}

                {userViewingType === "free_user_no_box" ||
                userViewingType === "guest_user_no_box" ? (
                  <Button
                    onClick={() => {
                      if (
                        userViewingType === "free_user_no_box" ||
                        userViewingType === "free_user_no_box_own_profile"
                      ) {
                        dispatch(
                          openUpgradePopup({
                            requiredSubscription: 2,
                          })
                        );
                      } else if (userViewingType === "guest_user_no_box") {
                        // Navigate to signup
                        const referralUrl = profileData?.data
                          ?.is_in_referral_program
                          ? `/signup/${profileData?.data?.code}`
                          : `/signup`;
                        window.location.href = referralUrl;
                      }
                    }}
                    sx={{
                      width:
                        userViewingType === "guest_user_no_box"
                          ? "50%"
                          : "243px",
                      height: "36px",
                      borderRadius: "8px",
                      opacity: 1,
                      padding: "8px 19px",
                      gap: "10px",
                      background: "#9B5AF8",
                      color: "#FFFFFF",
                      fontSize: "16px",
                      fontWeight: "500",
                      lineHeight: "20px",
                      fontFamily: "DM Sans",
                      textTransform: "none",
                      "&:hover": {
                        background: "#7A18E6",
                      },
                    }}
                  >
                    {userViewingType === "guest_user_no_box"
                      ? "Sign Up"
                      : "Upgrade Now"}
                  </Button>
                ) : null}
              </Box>
            )}
          </>
        )}
        <IconContainer isDefault={isDefault}>
          {iconsToShow.map((icon) =>
            icon.icon ? (
              <Box
                sx={
                  icon.link
                    ? {
                        cursor: "pointer",
                        transition: "transform 0.3s ease-in-out",
                        "&:hover": {
                          transform: "scale(1.2)",
                        },
                      }
                    : {
                        transition: "transform 0.3s ease-in-out",
                        "&:hover": {
                          transform: "scale(1.2)",
                        },
                      }
                }
                key={icon.id}
                onClick={() =>
                  icon.link &&
                  window.open(
                    /^(https?:\/\/)/.test(icon.link)
                      ? icon.link
                      : "https://" + icon.link,
                    "_blank"
                  )
                }
              >
                <img
                  src={icon.icon || ASSETS.website}
                  alt="link icon"
                  style={{
                    width: "24px",
                    height: "24px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : null
          )}
        </IconContainer>
        <Stack sx={{ width: "100%", mt: 2 }} spacing={"12px"}>
          {data?.ctaButtons?.map((button, index) => {
            if (!button.is_cta_enabled) {
              return null;
            }
            if (index === 0) {
              return (
                <Button
                  disabled={!button.is_cta_enabled}
                  onClick={() => {
                    button.link &&
                      window.open(
                        /^(https?:\/\/)/.test(button.link)
                          ? button.link
                          : "https://" + button.link,
                        "_blank"
                      );
                  }}
                  sx={{
                    backgroundColor: "#71B25A",
                    borderRadius: "8px",
                    color: "#FFFFFF",
                    fontSize: "16px",
                    fontWeight: "500",
                    lineHeight: "20px",
                    fontFamily: "DM Sans",
                    textTransform: "none",
                    py: "10px",

                    "&.Mui-disabled": {
                      backgroundColor: "#565556",
                      color: "#FFFFFF",
                    },

                    "&:hover": {
                      backgroundColor: colors.PrimaryPurple500,
                    },
                  }}
                  fullWidth
                >
                  {/* {transformedText(button.title) || `Button ${index}`} */}

                  <ButtonContent
                    dangerouslySetInnerHTML={{
                      __html: button.title || `Button ${index}`,
                    }}
                  />
                </Button>
              );
            } else {
              return (
                <Button
                  disabled={!button.is_cta_enabled}
                  onClick={() => {
                    button.link &&
                      window.open(
                        /^(https?:\/\/)/.test(button.link)
                          ? button.link
                          : "https://" + button.link,
                        "_blank"
                      );
                  }}
                  sx={{
                    backgroundColor: "transparent",
                    border: "2px solid #71B25A",
                    borderRadius: "8px",
                    color: "#71B25A",
                    fontSize: "16px",
                    fontWeight: "500",
                    lineHeight: "20px",
                    fontFamily: "DM Sans",
                    textTransform: "none",
                    py: "10px",

                    "&.Mui-disabled": {
                      backgroundColor: "#565556",
                      color: "#FFFFFF",
                    },

                    "&:hover": {
                      backgroundColor: colors.PrimaryPurple500,
                      color: "white",
                      border: `2px solid ${colors.PrimaryPurple500}`,
                    },
                  }}
                  fullWidth
                >
                  <ButtonContent
                    dangerouslySetInnerHTML={{
                      __html: button.title || `Button ${index}`,
                    }}
                  />
                </Button>
              );
            }
          }) || null}
        </Stack>
      </ViewOne>
    </div>
  );
};

DataBox.propTypes = {
  data: {
    imageSrc: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    color: PropTypes.string,
    ctaButtons: PropTypes.arrayOf(
      PropTypes.shape({
        is_cta_enabled: PropTypes.bool,
        link: PropTypes.string,
        title: PropTypes.string,
      })
    ),
    icons: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        src: PropTypes.string,
      })
    ),
  },
};

export default DataBox;
